import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from '../AuthContext';
import { motion } from "framer-motion";
import {
    Filter, X, Search, Package, ChevronLeft,
    ChevronRight, TrendingUp, TrendingDown,
    BarChart3, RefreshCw, ExternalLink,
    ArrowLeft
} from "lucide-react";
import http from "../../Api/http";
import Util from "../../Util";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../card/Card";
import StatusBadge from "./StatusBadge";
import Shipment from "./Shipment";
const PAGE_SIZE = 10;



// ===== Components =====
const Input = (props) => (
    <input {...props} className="border px-2 py-1 rounded-lg w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
);


// ===== Main =====
const ShipmentsReport = () => {
    const { t, i18n } = useTranslation();
    const language = i18n.language;
    const isRTL = language === "ar" || language === "he";
    const { user } = useAuth();
    const [dateFrom, setDateFrom] = useState(() => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        return Util.formatDateLocal(firstDay);

    });
    const [dateTo, setDateTo] = useState("");
    const [statusFilter, setStatusFilter] = useState(0);
    const [supplierFilter, setSupplierFilter] = useState(0);
    const [page, setPage] = useState(1);
    const [shipmentId, setShipmentId] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [openShipmentDialog, setOpenShipmentDialog] = useState(false);

    const [appliedFilters, setAppliedFilters] = useState({
        dateFrom: "",
        dateTo: "",
        status: "0",
        supplierId: "0",
    });
    const StatusList = [
        { Id: 1, Name: t('new') },
        { Id: 2, Name: t('sent') },
        { Id: 3, Name: t('received') },
        { Id: 4, Name: t('mustReturn') },
        { Id: 5, Name: t('returnedToSupplier') },
        { Id: 6, Name: t('approved') },
    ]

    const [shipments, setShipments] = useState([]);
    const [suppliers, setSuppliers] = useState([]);


    const totalAmount = shipments.reduce((s, x) => s + (x.TotalAmount || 0), 0);
    const receivedCount = shipments.filter(s => s.StatusId === 3).length;
    const pendingCount = shipments.filter(s => s.StatusId === 2 || s.StatusId === 1).length;

    const totalPages = Math.ceil(shipments.length / PAGE_SIZE);
    const paginated = shipments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleReset = async () => {
        const now = new Date();
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
        setDateFrom(Util.formatDateLocal(firstDay));
        setDateTo("");
        setStatusFilter(0);
        setSupplierFilter(user?.Role === 3 ? user.ID : 0);
        setAppliedFilters({ dateFrom: "", dateTo: "", status: 0, supplierId: 0 });


        await loadShipments(0, 0, firstDay, "");
    };

    useEffect(() => {
        if (user?.ID) {
            loadSuppliers();
            loadShipments();
            if (user?.Role === 3)
                setSupplierFilter(user?.ID)
        }
    }, [user]);

    const loadShipments = async (statusId = statusFilter, supplierId = supplierFilter, from = dateFrom, to = dateTo) => {
        try {
            setIsLoading(true);
            let obj = {
                SupplierId: supplierId,
                StatusId: statusId
            }
            if (from)
                obj.FromDate = from;
            if (to)
                obj.ToDate = to
            const res = await http.postJsonAxios(http.actions.Shipments.GetShipmentsReport, obj, t);
            if (res.status === 200 && res.data.success) {
                setShipments(res.data.obj);
                setPage(1);
            }
        } catch { }
        finally {
            setIsLoading(false);
        }
    };
    const loadSuppliers = async () => {
        const res = await http.getJsonAxios(http.actions.Shipments.GetSuppliers);
        if (res.status === 200 && res.data.success) {
            setSuppliers(res.data.dataObject);
        }
    };

    return (
        <div className="space-y-6 max-w-6xl mx-auto pt-10 pb-20" dir={t('dir')}>
            {/* Header */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center mb-16"
            >
                <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white">
                    {t('shipmentsReport')}
                </h1>
                <div className="h-1.5 w-24 bg-accent mx-auto rounded-full" />
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                </p>
            </motion.div>


            {/* Filters */}
            <Card>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">

                    {/* Dates */}
                    <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                    <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />

                    {/* Status */}
                    <select value={statusFilter} onChange={e => {
                        setStatusFilter(e.target.value);
                    }}
                        className="border px-2 py-1 rounded-lg w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" dir={t('dir')}>
                        <option value="">{t('selectStatus')}</option>
                        {StatusList.map(status => (
                            <option key={status.Id} value={status.Id}>
                                {status.Name}
                            </option>
                        ))}
                    </select>

                    {/* Supplier */}
                    <select disabled={user?.Role === 3} value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)}
                        className="border px-2 py-1 rounded-lg w-full border py-1 px-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" dir={t('dir')}>
                        <option value="all">{t('selectSupplier')}</option>
                        {suppliers.map(s => (
                            <option key={s.ID} value={s.ID}>{s.Name}</option>
                        ))}
                    </select>

                </div>

                <div className="flex gap-3 p-4 border-t">
                    <button className="bg-blue-500 text-white px-4 py-2 rounded flex items-center gap-2
                hover:bg-blue-700 shadow-lg shadow-blue-600/25" onClick={() => loadShipments()}><RefreshCw />{t('refresh')}</button>
                    <button className="bg-orange-500 text-white px-4 py-2 rounded flex items-center gap-2
                hover:bg-orange-700 shadow-lg shadow-orange-600/25" onClick={handleReset}><X /> {t('resetFilters')}</button>
                </div>
            </Card >

            {/* Stats Row */}
            < div className="grid grid-cols-1 sm:grid-cols-3 gap-4" >
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                    <Card className="border-border/50 shadow-sm">
                        <div className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">{t('shipmentsCount')}</p>
                                    <p className="text-3xl font-bold text-foreground">{shipments.length}</p>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                                    <Package className="w-5 h-5 text-primary" />
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <Card className="border-border/50 shadow-sm">
                        <div className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">{t('totalAmountSum')}</p>
                                    <p className="text-2xl font-bold text-foreground">
                                        {
                                            Util.formatNumber(totalAmount.toFixed(2))}{" "}{t('currency')
                                        }
                                    </p>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                    <Card className="border-border/50 shadow-sm">
                        <div className="p-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground mb-1">{t('statusSum')}</p>
                                    <p className="text-3xl font-bold text-foreground">
                                        <span className="text-amber-600">{pendingCount}</span>
                                        <span className="text-muted-foreground text-xl font-normal mx-1">/</span>
                                        <span className="text-emerald-600">{receivedCount}</span>
                                    </p>
                                </div>
                                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                                    <TrendingDown className="w-5 h-5 text-amber-600" />
                                </div>
                            </div>
                        </div>
                    </Card>
                </motion.div>
            </div >


            {/* Table Desktop */}
            < Card >
                <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('shipmentNo')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('theSupplier')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('createDate')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('totalItems')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('totalAmount')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide">{t('status')}</th>
                                <th className="text-center px-5 py-3 text-xs font-bold uppercase tracking-wide"></th>
                            </tr>
                        </thead>

                        <tbody>
                            {paginated.map((s, i) => (
                                <motion.tr
                                    key={s.id}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="hover:bg-muted/30 transition-colors group"
                                    onClick={() => {
                                        setShipmentId(s.Id);
                                        setOpenShipmentDialog(true);
                                    }}
                                >
                                    <td className="px-5 py-4">#{s.Id}</td>
                                    <td className="px-5 py-4">{s.SupplierName}</td>
                                    <td className="px-5 py-4">{Util.formatDate(s.InsDate, i18n.language)}</td>
                                    <td className="px-5 py-4">{s.ItemsCount}</td>
                                    <td className="px-5 py-4"> {s.TotalAmount ? Util.formatNumber(s.TotalAmount.toFixed(2)) : 0}{" "}
                                        {t('currency')}
                                    </td>
                                    <td className="px-5 py-4">
                                        <StatusBadge status={s.StatusId} />
                                    </td>
                                    <td className="px-5 py-4">
                                        <button
                                            className="inline-flex items-center text-blue-600 font-medium hover:text-green-700 transition"
                                        >
                                            {t('details')}
                                            <ArrowLeft className="w-4 h-4 ms-1 rtl:-scale-x-100 group-hover:-translate-x-1" />
                                        </button>
                                    </td>
                                </motion.tr>
                            ))}
                        </tbody>
                        {/* Total Row */}
                        {
                            paginated && paginated.length > 0 && (
                                <tfoot>
                                    <tr className="border-t-2 border-border bg-muted/20">
                                        <td colSpan={4} className="px-5 py-3 text-sm font-semibold text-foreground">
                                            {t('pagesTotal')} ({paginated.length} {t('shipment')})
                                        </td>
                                        <td className="px-5 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                                            {Util.formatNumber(paginated.reduce((s, sh) => s + (sh.TotalAmount ?? 0), 0))}{" "}
                                            {t('currency')}
                                        </td>
                                        <td colSpan={2} />
                                    </tr>
                                </tfoot>
                            )
                        }
                    </table>
                </div>

                {/* Mobile */}
                <div className="sm:hidden">
                    {paginated.map((s) => (
                        <div
                            key={s.id}
                            className="p-4 border-b"
                            onClick={() => {
                                setShipmentId(s.Id);
                                setOpenShipmentDialog(true);
                            }}
                        >
                            <div className="flex justify-between">
                                <b>#{s.Id}</b>
                                <button
                                    className="inline-flex items-center text-blue-600 font-medium hover:text-green-700 transition"
                                >
                                    {t('details')}
                                    <ArrowLeft className="w-4 h-4 ms-1 rtl:-scale-x-100 group-hover:-translate-x-1" />
                                </button>
                            </div>
                            <div>{t('theSupplier')}{": "}{s.SupplierName}</div>
                            <div>{t('createDate')}{": "}{Util.formatDate(s.InsDate, i18n.language)}</div>
                            <div>{t('totalItems')}{": "}{s.ItemsCount}</div>
                            <div>
                                {t('totalAmount')}{": "}{s.TotalAmount ? Util.formatNumber(s.TotalAmount.toFixed(2)) : 0} {" "}
                                {t('currency')}
                            </div>
                            <td className="px-5 py-4">
                                <StatusBadge status={s.StatusId} />
                            </td>
                        </div>
                    ))}
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex justify-between p-4 border-t">
                        {/* <Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        <ChevronRight /> السابق
                    </Button> */}
                        <button disabled={page <= 1} className="border border-blue-500 text-blue-500 bg-transparent px-4 py-2 rounded flex items-center gap-2
                        hover:bg-blue-700 hover:text-white shadow-lg shadow-blue-600/25 transition" onClick={() => setPage(p => p - 1)}>
                            {isRTL ? <ChevronRight /> : <ChevronLeft />}{t('prev')}
                        </button>

                        <span>{page} / {totalPages}</span>
                        {/* 
                    <Button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                        التالي <ChevronLeft />
                    </Button> */}

                        <button disabled={page >= totalPages} className="border border-blue-500 text-blue-500 bg-transparent px-4 py-2 rounded flex items-center gap-2
                        hover:bg-blue-700 hover:text-white shadow-lg shadow-blue-600/25 transition" onClick={() => setPage(p => p + 1)}>
                            {t('next')}
                            {isRTL ? <ChevronLeft /> : <ChevronRight />}
                        </button>
                    </div>
                )
                }

            </Card >
            {/* Shipment Dialog */}
            {
                openShipmentDialog &&
                <Shipment
                    open={openShipmentDialog}
                    onClose={() => {
                        setOpenShipmentDialog(false);
                        loadShipments(statusFilter);
                    }}
                    shipmentId={shipmentId}  // 0 تعني شحنة جديدة
                />
            }
        </div >
    );
}
export default ShipmentsReport;